import { DropListWithIconItem, MarketRate, MinAmounts } from "@cede/types";
import { API_ENDPOINT, getCurrentRate, getErrorMessage } from "@cede/utils";
import { Precise } from "@cede/utils";
import { useTradePath } from "../api/useTradePath";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { AlertTypes } from "@/store";
import { useAlert } from "@/store/alert/alert";
import { useOrderBookStore } from "@/store/orderBookStore";
import { computeMinAmountInFromCurrency } from "@/utils/trade/minAmount";
import { useQuery } from "@tanstack/react-query";

type MultipleTransactionsMinAmountProps = {
  enabled: boolean;
  accountId: string;
  fromToken: string;
  toToken: string;
  isInternalTransfer?: boolean;
  network?: DropListWithIconItem<{
    withdrawMin?: string;
    withdrawFee?: string;
  }>;
};

/**
 * @description Get the biggest min amount for the orders and the withdrawal in the original currency
 *
 */
export const useMultipleTransactionsMinAmount = ({
  enabled,
  accountId,
  network,
  fromToken,
  toToken,
  isInternalTransfer: _isInternalTransfer = false,
}: MultipleTransactionsMinAmountProps) => {
  const apiHelper = useApi();
  const { auth, hubUser } = useHubUser();
  const orderBook = useOrderBookStore((state) => state.orderBook);
  const { tradePath, isLoading: tradePathIsLoading } = useTradePath({
    enabled,
    accountId,
    fromToken,
    toToken,
    network: network?.value,
  });
  const appendAlert = useAlert((state) => state.append);

  // Withdrawals are not supported on this platform, so no need to fetch networks
  // For trading-only mode (activeWithdrawal=false), we skip network-related calculations

  const _computeBiggestMinAmount = async () => {
    if (!tradePath) return "0";
    try {
      const safeMargin = "0.2";
      // Start with "0" - no withdrawal minimums since withdrawals are not supported
      let _minAmounts: string[] = ["0"];

      // We need to reverse the trade path in order to start from the final currency
      // and convert through the iterations to the first one
      for (const tradePathData of [...tradePath].reverse()) {
        const { pairSymbol, orderSide } = tradePathData;

        const account = hubUser?.exchangeAccounts.find((acc) => acc.id === Number(accountId));
        if (!account) continue;

        let marketRate: MarketRate = { bid: "0", ask: "0" };

        // Check orderBookStore cache first
        if (orderBook && orderBook.pairSymbol === pairSymbol && orderBook.exchangeId === account.exchangeId) {
          marketRate = {
            bid: String(orderBook.bids[0]?.[0] || 0),
            ask: String(orderBook.asks[0]?.[0] || 0),
          };
        } else if (auth.identityToken) {
          // Fallback to API
          marketRate = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_PUBLIC_MARKET_RATE, {
            queryParams: {
              pairSymbol,
              exchangeId: account.exchangeId,
            },
          });
        }

        const price = getCurrentRate(marketRate, orderSide);
        // We compute all the min amounts in the current currency
        // We do the opposite operation regarding the classic order since we are reverting the order side
        // If the orderSide is "buy" we need to multiply the min amount by ask price of the market
        // If the orderSide is "sell" we need to divide the min amount by bid price of the market
        if (orderSide === "buy") {
          _minAmounts = _minAmounts.map((minAmount) => Precise.stringMul(minAmount || "0", price) || "0");
        } else {
          _minAmounts = _minAmounts.map((minAmount) => Precise.stringDiv(minAmount || "0", price) || "0");
        }
        // We add the min trade amount to the array
        // We do it at the end of the loop to avoid converting the min trade amount in the current currency
        // because it's already in the current currency
        if (!auth.identityToken) continue;

        const minTradeAmount: MinAmounts = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_TRADE_MIN_AMOUNTS, {
          queryParams: {
            accountId,
            pairSymbol,
            orderSide,
            price,
          },
        });

        const minAmountForLeg = computeMinAmountInFromCurrency(minTradeAmount, orderSide, price);
        _minAmounts.push(minAmountForLeg.toString());
      }
      const maxMinAmount = String(Math.max(..._minAmounts.map((minAmount) => parseFloat(minAmount || "0"))));
      const finalMinAmount = Precise.stringAdd(maxMinAmount, Precise.stringMul(maxMinAmount, safeMargin) || "0");
      return finalMinAmount;
    } catch (error) {
      appendAlert(getErrorMessage(error), AlertTypes.ERROR);
      return "0";
    }
  };

  const { data: minAmount, isFetching } = useQuery({
    queryKey: ["minAmount", tradePath, accountId, auth.identityToken],
    queryFn: _computeBiggestMinAmount,
    enabled: !!auth.identityToken && !!tradePath && tradePath.length > 0 && enabled, // Trading mode only
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return {
    minAmount,
    isLoading: isFetching || tradePathIsLoading,
    tradePath: tradePath || [],
  };
};
